// MatrixEditor — interactive drag-and-draw family/entity chart powered by
// React Flow. The fourth tab inside Entities & Roles, alongside the auto
// matrix and the uploaded PDF chart.
//
// On first open, we seed the canvas from the client's Individuals + Entities
// (laid out in a simple grid). The user can drag boxes, connect them with
// arrows, edit labels, and Save — the layout persists per-client.
import { useEffect, useState, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import ReactFlow, {
  Background, Controls, MiniMap, addEdge,
  applyNodeChanges, applyEdgeChanges,
  Handle, Position, MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Save, Loader2, RotateCcw, Plus, Users, Building2, Trash2, Sparkles, Download,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ──────────────────────────────────────────────────────────────────────────
// Custom node components — keeps styling consistent with the rest of the app
// ──────────────────────────────────────────────────────────────────────────

function IndividualNode({ data }) {
  return (
    <div className="bg-[#FFF8F0] border-2 border-[#E6D9C4] rounded-xl px-4 py-3 min-w-[140px] shadow-sm" data-testid={`flow-node-individual-${data.label}`}>
      <Handle type="target" position={Position.Top} className="!bg-[#A86A3A]" />
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-full bg-[#A86A3A] text-white flex items-center justify-center text-xs font-medium">
          {(data.label || '?').charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-[#2C2420] truncate">{data.label}</p>
          {data.subtitle && <p className="text-[10px] text-[#A86A3A] truncate">{data.subtitle}</p>}
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-[#A86A3A]" />
    </div>
  );
}

function EntityNode({ data }) {
  return (
    <div className="bg-white border-2 border-[#4A3B32] rounded-xl px-4 py-3 min-w-[160px] shadow-sm" data-testid={`flow-node-entity-${data.label}`}>
      <Handle type="target" position={Position.Top} className="!bg-[#4A3B32]" />
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-md bg-[#4A3B32] text-white flex items-center justify-center">
          <Building2 className="w-3.5 h-3.5" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#2C2420] truncate">{data.label}</p>
          {data.subtitle && <p className="text-[10px] text-[#5D524D] truncate">{data.subtitle}</p>}
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-[#4A3B32]" />
    </div>
  );
}

const NODE_TYPES = { individual: IndividualNode, entity: EntityNode };

// ──────────────────────────────────────────────────────────────────────────
// Main editor
// ──────────────────────────────────────────────────────────────────────────

export default function MatrixEditor() {
  const { clientId, client } = useOutletContext();
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const dirtyRef = useRef(false);

  const markDirty = useCallback(() => {
    if (!dirtyRef.current) {
      dirtyRef.current = true;
      setDirty(true);
    }
  }, []);

  // ─── load layout (or seed from individuals + entities)
  const load = useCallback(async () => {
    if (!token || !clientId) return;
    setLoading(true);
    try {
      const layoutRes = await axios.get(`${BACKEND_URL}/api/clients/${clientId}/matrix-layout`, { headers });
      const layout = layoutRes.data;
      // Defensive sanitiser — drop any node missing a numeric position so
      // React Flow doesn't crash on corrupt/legacy data.
      const sanitisedNodes = (layout.nodes || []).filter((n) =>
        n && n.position && typeof n.position.x === 'number' && typeof n.position.y === 'number',
      );
      if (sanitisedNodes.length > 0) {
        setNodes(sanitisedNodes);
        setEdges(layout.edges || []);
        setLastSaved(layout.updatedAt);
        dirtyRef.current = false; setDirty(false);
      } else {
        // Seed from existing client data
        const [indRes, entRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/clients/${clientId}/individuals`, { headers }).catch(() => ({ data: { individuals: [] } })),
          axios.get(`${BACKEND_URL}/api/clients/${clientId}/entities`, { headers }).catch(() => ({ data: { entities: [] } })),
        ]);
        const inds = indRes.data?.individuals || indRes.data || [];
        const ents = entRes.data?.entities || entRes.data || [];
        setNodes([
          ...inds.slice(0, 20).map((i, idx) => ({
            id: `ind-${i.id || idx}`,
            type: 'individual',
            position: { x: 60 + (idx % 5) * 180, y: 80 + Math.floor(idx / 5) * 120 },
            data: { label: i.name || 'Individual', subtitle: i.role || i.relationship || '' },
          })),
          ...ents.slice(0, 20).map((e, idx) => ({
            id: `ent-${e.id || idx}`,
            type: 'entity',
            position: { x: 60 + (idx % 5) * 180, y: 360 + Math.floor(idx / 5) * 120 },
            data: { label: e.name || 'Entity', subtitle: e.type || e.entityType || '' },
          })),
        ]);
        setEdges([]);
        dirtyRef.current = false; setDirty(false);
      }
    } catch (err) {
      toast.error('Could not load matrix editor');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, clientId]);

  useEffect(() => { load(); }, [load]);

  // ─── react-flow handlers
  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
    // Only mark dirty on user-driven changes — dimension auto-measurements
    // fire on mount and would falsely show "Unsaved changes" immediately.
    if (changes.some((c) => ['position', 'remove'].includes(c.type))) markDirty();
  }, [markDirty]);
  const onEdgesChange = useCallback((changes) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
    if (changes.some((c) => ['add', 'remove', 'reset'].includes(c.type))) markDirty();
  }, [markDirty]);
  const onConnect = useCallback((conn) => {
    setEdges((eds) => addEdge({ ...conn, markerEnd: { type: MarkerType.ArrowClosed, color: '#4A3B32' }, style: { stroke: '#4A3B32', strokeWidth: 1.5 } }, eds));
    markDirty();
  }, [markDirty]);

  // ─── add new node
  const addNode = (type) => {
    const id = `${type[0]}-new-${Date.now()}`;
    const newNode = {
      id,
      type,
      position: { x: 240, y: 240 },
      data: { label: type === 'individual' ? 'New Person' : 'New Entity', subtitle: '' },
    };
    setNodes((n) => [...n, newNode]);
    setSelectedId(id);
    markDirty();
  };

  const deleteSelected = () => {
    if (!selectedId) return;
    setNodes((n) => n.filter((x) => x.id !== selectedId));
    setEdges((e) => e.filter((x) => x.source !== selectedId && x.target !== selectedId));
    setSelectedId(null);
    markDirty();
  };

  const updateLabel = (field, value) => {
    setNodes((n) => n.map((x) => x.id === selectedId ? { ...x, data: { ...x.data, [field]: value } } : x));
    markDirty();
  };

  // ─── save
  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${BACKEND_URL}/api/clients/${clientId}/matrix-layout`, { nodes, edges }, { headers });
      setLastSaved(r.data?.updatedAt);
      dirtyRef.current = false; setDirty(false);
      toast.success('Layout saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ─── apply AI chart suggestions to the editor canvas
  const importFromChart = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/clients/${clientId}/matrix-chart/extractions?status=pending`, { headers });
      const items = r.data?.items || [];
      if (items.length === 0) {
        toast.info('No pending chart suggestions to import. Upload a chart first.');
        return;
      }
      const newNodes = items.flatMap((ext, idx) => {
        const p = ext.payload || {};
        if (ext.kind === 'individual') {
          return [{
            id: `ind-imp-${ext.id}`, type: 'individual',
            position: { x: 60 + (idx % 5) * 180, y: 80 + Math.floor(idx / 5) * 120 },
            data: { label: p.name, subtitle: p.role || '' },
          }];
        }
        if (ext.kind === 'entity') {
          return [{
            id: `ent-imp-${ext.id}`, type: 'entity',
            position: { x: 60 + (idx % 5) * 180, y: 360 + Math.floor(idx / 5) * 120 },
            data: { label: p.name, subtitle: p.type || '' },
          }];
        }
        return [];
      });
      setNodes((existing) => [...existing, ...newNodes]);
      toast.success(`Imported ${newNodes.length} from AI chart suggestions`);
      markDirty();
    } catch {
      toast.error('Could not import chart suggestions');
    }
  };

  const selectedNode = nodes.find((n) => n.id === selectedId);

  if (loading) {
    return (
      <div className="flex justify-center py-20" data-testid="matrix-editor-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#4A3B32]" />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="matrix-editor">
      {/* Toolbar */}
      <div className="bg-white rounded-2xl border border-[#E6E0D9] p-3 flex items-center gap-2 flex-wrap" data-testid="matrix-editor-toolbar">
        <Button size="sm" variant="outline" onClick={() => addNode('individual')} className="gap-1.5" data-testid="add-individual-btn">
          <Plus className="w-3.5 h-3.5" /><Users className="w-3.5 h-3.5" /> Person
        </Button>
        <Button size="sm" variant="outline" onClick={() => addNode('entity')} className="gap-1.5" data-testid="add-entity-btn">
          <Plus className="w-3.5 h-3.5" /><Building2 className="w-3.5 h-3.5" /> Entity
        </Button>
        <Button size="sm" variant="outline" onClick={importFromChart} className="gap-1.5" data-testid="import-from-chart-btn">
          <Sparkles className="w-3.5 h-3.5 text-[#A86A3A]" /> Import from AI chart
        </Button>
        <Button size="sm" variant="ghost" onClick={load} className="gap-1.5 text-[#8C8078]" data-testid="reset-editor-btn">
          <RotateCcw className="w-3.5 h-3.5" /> Discard changes
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-[#8C8078]">
            {dirty
              ? 'Unsaved changes'
              : lastSaved
              ? `Saved · ${new Date(lastSaved).toLocaleString()}`
              : 'No layout saved yet'}
          </span>
          <Button size="sm" onClick={save} disabled={saving || !dirty} className="bg-[#4A3B32] hover:bg-[#3A2E26] gap-1.5" data-testid="save-layout-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-3">
        {/* Canvas */}
        <div className="bg-white rounded-2xl border border-[#E6E0D9] overflow-hidden" style={{ height: '600px' }} data-testid="matrix-editor-canvas">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={NODE_TYPES}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={(_, n) => setSelectedId(n.id)}
            onPaneClick={() => setSelectedId(null)}
            fitView
            attributionPosition="bottom-left"
          >
            <Background color="#E6E0D9" gap={16} />
            <Controls />
            <MiniMap nodeColor={(n) => n.type === 'individual' ? '#A86A3A' : '#4A3B32'} maskColor="#fbfaf8aa" />
          </ReactFlow>
        </div>

        {/* Inspector */}
        <aside className="bg-white rounded-2xl border border-[#E6E0D9] p-4 space-y-3" data-testid="matrix-editor-inspector">
          <h3 className="text-sm font-medium text-[#2C2420]">Inspector</h3>
          {!selectedNode ? (
            <p className="text-xs text-[#8C8078] leading-relaxed">
              Select a box on the canvas to edit its label, or drag from one
              handle to another to connect two boxes. Tip: <strong>{client?.name}</strong> needs
              a save before changes persist.
            </p>
          ) : (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#8C8078] block mb-1">Label</label>
                <Input
                  value={selectedNode.data?.label || ''}
                  onChange={(e) => updateLabel('label', e.target.value)}
                  data-testid="inspector-label-input"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#8C8078] block mb-1">Subtitle</label>
                <Input
                  value={selectedNode.data?.subtitle || ''}
                  onChange={(e) => updateLabel('subtitle', e.target.value)}
                  placeholder={selectedNode.type === 'individual' ? 'Role (e.g. Patriarch)' : 'Type (e.g. Trust)'}
                  data-testid="inspector-subtitle-input"
                />
              </div>
              <div className="pt-1">
                <p className="text-[10px] text-[#8C8078] mb-2">
                  Type: <strong>{selectedNode.type === 'individual' ? 'Individual' : 'Entity'}</strong>
                </p>
                <Button size="sm" variant="ghost" onClick={deleteSelected} className="w-full text-red-600 hover:bg-red-50 gap-1.5" data-testid="delete-node-btn">
                  <Trash2 className="w-3.5 h-3.5" /> Delete this node
                </Button>
              </div>
            </>
          )}
          <hr className="border-[#F0EAE3]" />
          <p className="text-[10px] text-[#8C8078] leading-relaxed">
            <Download className="w-3 h-3 inline mr-1" />
            Export-as-PNG coming soon. For now use your browser's screenshot tool.
          </p>
        </aside>
      </div>
    </div>
  );
}
